import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Breadcrumbs, Link, makeStyles } from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';

/**
 * PageToolbar -- ang iisang header ng LAHAT ng page, kasama ang dashboard.
 *
 * Dati, may sariling kopya ng buong header ang bawat page folder (50 file,
 * ~4,300 linya). Isa na lang ngayon, kaya isang file lang ang babaguhin
 * kapag may aayusin sa disenyo.
 *
 * Gamit:
 *
 *   <PageToolbar
 *     icon={<CreateNewFolderIcon />}
 *     title="Create auction"
 *     subtitle="Set up a new auction room"
 *     breadcrumbs={[
 *       { label: 'Dashboard', to: '/app/dashboard', icon: <DashboardIcon /> },
 *       { label: 'Auctions', to: '/app/auctions/list' },
 *       { label: 'Create' },
 *     ]}
 *     actions={<Button>Save</Button>}
 *   />
 *
 * Ang breadcrumb na walang `to` ay hindi clickable -- ito ang kasalukuyang page.
 * Opsyonal ang `breadcrumbs`, `icon`, `subtitle`, at `actions`.
 */
const ACCENT = '#3e4396';

const useStyles = makeStyles((theme) => ({
 
  root: {
     backgroundColor: '#fff',
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(4),
    // Hairline rule sa halip na naka-card na header. Dekorasyon lang ang
    // card; ang rule ay may trabaho -- naghahati ng header at content.
    borderBottom: `2px solid ${ACCENT}`,
  },
  breadcrumbs: {
    marginBottom: theme.spacing(1),
    fontSize: 13,
  },
  crumb: {
    display: 'flex',
    alignItems: 'center',
    color: '#6b6b7b',
    '& svg': { fontSize: 16, marginRight: 4 },
  },
  crumbCurrent: {
    color: '#1a1a2e',
    fontWeight: 600,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    flexShrink: 0,
    marginRight: theme.spacing(1.75),
    borderRadius: 6,
    backgroundColor: 'rgba(62, 67, 150, 0.08)',
    color: ACCENT,
    '& svg': { fontSize: 22 },
  },
  title: {
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: '#1a1a2e',
    lineHeight: 1.15,
  },
  subtitle: {
    marginTop: 2,
    color: '#5b5b6b',
  },
}));

const PageToolbar = ({
  title,
  subtitle,
  icon,
  breadcrumbs,
  actions,
  className,
  ...rest
}) => {
  const classes = useStyles();

  return (
    <Box className={clsx(classes.root, className)} {...rest}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon style={{ fontSize: 16 }} />}
          aria-label="breadcrumb"
          className={classes.breadcrumbs}
        >
          {breadcrumbs.map((crumb) => {
            const content = (
              <>
                {crumb.icon}
                {crumb.label}
              </>
            );

            // Walang `to` = kasalukuyang page, kaya hindi clickable.
            return crumb.to ? (
              <Link
                key={crumb.label}
                component={RouterLink}
                to={crumb.to}
                className={classes.crumb}
                color="inherit"
              >
                {content}
              </Link>
            ) : (
              <Typography
                key={crumb.label}
                className={clsx(classes.crumb, classes.crumbCurrent)}
              >
                {content}
              </Typography>
            );
          })}
        </Breadcrumbs>
      )}

      <Box className={classes.titleRow}>
        <Box className={classes.titleGroup}>
          {icon && <span className={classes.iconBox}>{icon}</span>}
          <Box minWidth={0}>
            <Typography variant="h4" className={classes.title}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" className={classes.subtitle}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {actions && <Box>{actions}</Box>}
      </Box>
    </Box>
  );
};

PageToolbar.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string,
      icon: PropTypes.node,
    })
  ),
  actions: PropTypes.node,
  className: PropTypes.string,
};

export default PageToolbar;